import { Head, Link, useForm } from '@inertiajs/react'
import { SubmitEvent } from 'react'
import { Box, Button, Callout, Flex, Text } from '@radix-ui/themes'
import { Info } from 'lucide-react'
import MediaGroupForm from '@/pages/media_group_participants/_form'
import StaffForm from '@/pages/staff_participants/_form'
import DriverCarForm from '@/pages/driver_cars/_form'
import CredentialGatePicker from '@/shared/credentials/CredentialGatePicker'
import {
  ApplicationsNewDriverCarProps,
  ApplicationsNewMediaGroupProps,
  ApplicationsNewProps,
  ApplicationsNewStaffProps,
} from '@/types/applications_new'
import { DriverCarFormData } from '@/pages/driver_cars/_form'
import { StaffFormData } from '@/pages/staff_participants/_form'
import { MediaGroupFormData } from '@/types/shared'

function MediaGroupApplication({
  event,
  event_path,
  participant_id,
  participant,
  media_group,
  credential_gate,
  credential_category,
  errors,
}: ApplicationsNewMediaGroupProps) {
  const { data, setData, post, processing } = useForm<
    MediaGroupFormData & { participant_id: number; credential_category: string | null }
  >({
    participant_id,
    participant,
    media_group,
    credential_category,
  })

  const selected = credential_gate?.options.find((o) => o.category === data.credential_category) ?? null

  // Pending is enough to apply — waiting on a manager would send the last-minute
  // applicant away. Rejected is not: it has to be fixed first. We hide the
  // application form until then rather than let someone fill it out and lose it
  // on the way to the annual form.
  const holdsCredential = selected?.status === 'pending' || selected?.status === 'approved'
  const needsCredential = Boolean(credential_gate) && !holdsCredential

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault()
    post(`/events/${event.id}/applications`)
  }

  return (
    <Flex direction="column" gap="5">
      {credential_gate && (
        <Box className="border-2 py-8 px-10">
          <CredentialGatePicker
            gate={credential_gate}
            value={data.credential_category}
            onChange={(category) => setData('credential_category', category)}
          />

          {!selected && (
            <Text size="3" color="gray">
              Pick one to continue.
            </Text>
          )}

          {selected && !selected.status && (
            <Flex direction="column" gap="3" align="start">
              <Callout.Root color="gray" variant="soft" size="2">
                <Callout.Icon>
                  <Info size={18} />
                </Callout.Icon>
                <Callout.Text>
                  You'll need {credential_gate.track_name}'s {selected.title} first. It takes a couple of
                  minutes, and you'll come straight back here to finish applying to {event.name}.
                </Callout.Text>
              </Callout.Root>
              <Button size="3" asChild>
                <Link href={selected.form_path}>Start the Application Process</Link>
              </Button>
            </Flex>
          )}

          {/* Sent back — fix the answers in place rather than starting over. */}
          {selected?.status === 'rejected' && selected.edit_path && (
            <Flex direction="column" gap="3" align="start">
              <Callout.Root color="amber" variant="soft" size="2">
                <Callout.Icon>
                  <Info size={18} />
                </Callout.Icon>
                <Callout.Text>
                  {credential_gate.track_name} sent your {selected.title} back
                  {selected.rejection_reason ? (
                    <>
                      : <Text weight="bold">{selected.rejection_reason}</Text>
                    </>
                  ) : (
                    <> without saying why — reach out to them if you're not sure.</>
                  )}{' '}
                  Your answers are still there; change what's wrong and send it back.
                </Callout.Text>
              </Callout.Root>
              <Button size="3" asChild>
                <Link href={selected.edit_path}>Fix your answers</Link>
              </Button>
            </Flex>
          )}

          {selected?.status === 'pending' && (
            <Callout.Root color="gray" variant="soft" size="2">
              <Callout.Icon>
                <Info size={18} />
              </Callout.Icon>
              <Callout.Text>
                Your {selected.title} is waiting on {credential_gate.track_name} to review it. You can still
                apply — they'll review both together.
              </Callout.Text>
            </Callout.Root>
          )}
        </Box>
      )}

      {!needsCredential && (
        <MediaGroupForm
          data={data}
          setData={setData}
          onSubmit={handleSubmit}
          processing={processing}
          errors={errors}
          title={`Apply to ${event.name}`}
          subtitle="Just confirm that nothing has changed and click Apply."
          submitLabel="Apply"
          cancelPath={event_path}
        />
      )}
    </Flex>
  )
}

function StaffApplication({ event, event_path, participant_id, participant, staff, errors }: ApplicationsNewStaffProps) {
  const { data, setData, post, processing } = useForm<StaffFormData & { participant_id: number }>({
    participant_id,
    participant,
    staff,
  })

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault()
    post(`/events/${event.id}/applications`)
  }

  return (
    <StaffForm
      data={data}
      setData={setData}
      onSubmit={handleSubmit}
      processing={processing}
      errors={errors}
      title={`Apply to ${event.name}`}
      subtitle="Just confirm that nothing has changed and click Apply."
      submitLabel="Apply"
      cancelPath={event_path}
    />
  )
}

function DriverCarApplication({ event, event_path, participant_id, participant, driver_car, color_families, aspirations, errors }: ApplicationsNewDriverCarProps) {
  const { data, setData, post, processing } = useForm<DriverCarFormData & { participant_id: number }>({
    participant_id,
    participant,
    driver_car,
  })

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault()
    post(`/events/${event.id}/applications`)
  }

  return (
    <DriverCarForm
      data={data}
      setData={setData}
      onSubmit={handleSubmit}
      processing={processing}
      errors={errors}
      title={`Apply to ${event.name}`}
      subtitle="Just confirm that nothing has changed and click Apply."
      submitLabel="Apply"
      colorFamilies={color_families}
      aspirations={aspirations}
    />
  )
}

export default function NewApplication(props: ApplicationsNewProps) {
  return (
    <>
      <Head title={`Apply to ${props.event.name}`} />
      <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
        {props.participant_type === 'Staff' && <StaffApplication {...props} />}
        {props.participant_type === 'DriverCar' && <DriverCarApplication {...props} />}
        {props.participant_type === 'MediaGroup' && <MediaGroupApplication {...props} />}
      </div>
    </>
  )
}
